import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { Timer, CreditCard, LockSimple as Lock } from '@phosphor-icons/react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { contentsApi } from '../../../api/contents';
import { checkoutsApi } from '../../../api/checkouts';
import { paymentsApi } from '../../../api/payments';
import { PageBlocks, useCheckoutPage } from '../components/PageBlocks';
import { checkoutButtonColors } from '../components/PageBlocks/appearance';
import {
	getBehaviorSessionId,
	initBehaviorTracker,
	trackBehavior
} from '../components/PageBlocks/behavior-tracker';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { apiFactory } from '../../../api/ApiFactory';
import { usePlatformApiKey } from '../components/hooks/usePlatformApiKey';
import { usePageReady } from '../components/hooks/usePageReady';
import { formatCurrency } from '../../../utils/formatCurrency';
import { addOrderExtra } from '../Thanks/order-extras';
import { ConfirmDialog } from './ConfirmDialog';
import '../Thanks/styles.scss';
import './styles.scss';
import { reportCheckoutError } from '../../../utils/sentry-checkout';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import {
	arePaymentsUnavailable,
	isSellerNotActiveError
} from '../../../utils/payments-availability';

const { getBySlug, getById } = contentsApi;
const { findByContent } = checkoutsApi;

/**
 * Oferta one-click pós-compra ({platform}/p/upsell/{slug} e /p/downsell/).
 * Upsell aparece quando a compra no cartão foi APROVADA; recusou e há
 * downsell configurado, cai na oferta alternativa antes do obrigado.
 * Aceitar cobra one-click no cartão tokenizado da compra (o token curto vem
 * do close-payment via sessionStorage).
 */
export const UpsellPage = ({
	variant = 'upsell'
}: {
	variant?: 'upsell' | 'downsell';
}) => {
	const { slug, platform } = useParams();
	const isApiKeyReady = usePlatformApiKey(platform);
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState<
		'idle' | 'charging' | 'success' | 'error' | 'expired'
	>('idle');
	const [errorMessage, setErrorMessage] = useState('');
	const { finance } = useContext(ApplicationContext);
	// Vendedor em análise ou pausado (pay #619): a compra principal já passou,
	// então não há o que travar na frente do comprador — a oferta só não
	// aparece e ele segue para o obrigado.
	const paymentsUnavailable = arePaymentsUnavailable(finance, {
		isPreview: isPreviewMode()
	});

	const {
		data: content,
		isFetched: contentFetched,
		isError: contentFailed
	} = useQuery(
		[getBySlug.key, { slug: slug ?? '' }],
		getBySlug.fetch,
		// Só depois da api_key da plataforma: sem ela o conteúdo volta 401.
		{ refetchOnWindowFocus: false, enabled: isApiKeyReady }
	);
	const {
		data: checkout,
		isFetched: checkoutFetched,
		isError: checkoutFailed
	} = useQuery(
		[findByContent.key, { id: content?.id }],
		findByContent.fetch,
		{ refetchOnWindowFocus: false }
	);
	const page = useCheckoutPage(checkout?.id);
	const upsell = page?.settings?.[variant];
	const downsell = page?.settings?.downsell;

	// Vitrine da oferta (título, imagem, preço de tabela) — aparece na hora,
	// inclusive no preview do builder. O valor cobrado vem do preview do pay.
	const { data: offerContent } = useQuery(
		[getById.key, { id: Number(upsell?.contentId) }],
		getById.fetch,
		{
			enabled: isApiKeyReady && !!upsell?.contentId,
			refetchOnWindowFocus: false,
			retry: 1
		}
	);

	// Mesma sessão de eventos do checkout (a chave de sessionStorage é por
	// checkoutId) — o funil upsell_shown/accepted/declined emenda no funil
	// da compra na inteligência de conversão.
	useEffect(() => {
		if (!checkout?.id || isPreviewMode()) return;
		apiFactory
			.getInstance()
			.getApiKey()
			.then((apiKey) =>
				initBehaviorTracker({
					checkoutId: checkout.id,
					payUrl: process.env.REACT_APP_CURSEDUCA_PAY_DOMAIN || '',
					apiKey
				})
			)
			.catch(() => undefined);
	}, [checkout?.id]);

	useEffect(() => {
		if (!upsell?.enabled || !upsell?.contentId || isPreviewMode()) return;
		trackBehavior(
			variant === 'downsell' ? 'downsell_shown' : 'upsell_shown',
			undefined,
			String(upsell.contentId),
			{ once: true }
		);
	}, [upsell?.enabled, upsell?.contentId, variant]);

	// Token e query do obrigado gravados pelo checkout no redirect (a chave é
	// por caminho — o desvio pro downsell copia o token pra chave nova).
	const stored = useMemo(() => {
		try {
			return JSON.parse(
				sessionStorage.getItem(
					`checkout-upsell:${window.location.pathname}`
				) || '{}'
			) as { token?: string; thanksQuery?: string };
		} catch {
			return {} as { token?: string; thanksQuery?: string };
		}
	}, []);

	const thanksPath = window.location.pathname.replace(
		`/p/${variant}/`,
		'/p/thanks/'
	);
	// Comprovante da compra principal: as ofertas aceitas ficam presas a ela.
	const mainReceipt = new URLSearchParams(
		stored.thanksQuery || searchParams.toString()
	).get('receipt');
	const goToThanks = () => {
		const query = stored.thanksQuery || searchParams.toString();
		window.location.replace(`${thanksPath}${query ? `?${query}` : ''}`);
	};

	/** Recusa: upsell com downsell configurado desvia pra oferta alternativa. */
	const decline = () => {
		if (
			variant === 'upsell' &&
			downsell?.enabled &&
			downsell?.contentId &&
			stored.token
		) {
			const downsellPath = window.location.pathname.replace(
				'/p/upsell/',
				'/p/downsell/'
			);
			try {
				sessionStorage.setItem(
					`checkout-upsell:${downsellPath}`,
					JSON.stringify(stored)
				);
				const query = stored.thanksQuery || searchParams.toString();
				window.location.replace(
					`${downsellPath}${query ? `?${query}` : ''}`
				);
				return;
			} catch {
				// sem storage, segue direto pro obrigado
			}
		}
		goToThanks();
	};

	/**
	 * O que vai ser cobrado, ANTES do clique: produto, valor (1x, com o
	 * desconto de cartão do produto), cartão e até quando vale. Mesma
	 * autorização da cobrança — se ela não passa, a oferta nem aparece.
	 */
	const canPreview =
		!isPreviewMode() &&
		!paymentsUnavailable &&
		!!stored.token &&
		!!upsell?.enabled &&
		!!upsell?.contentId;
	const { data: preview, isError: previewFailed } = useQuery(
		['upsell-preview', variant, upsell?.contentId],
		() =>
			paymentsApi.upsellPreview({
				token: stored.token as string,
				contentId: Number(upsell?.contentId)
			}),
		{ enabled: canPreview, retry: 1, refetchOnWindowFocus: false }
	);

	// Skeleton até a página do builder e o preço responderem: sem isso a tela
	// mostra os textos padrão e troca pelos do admin um instante depois.
	const isReady = usePageReady({
		contentSettled: contentFetched || contentFailed,
		hasContent: !!content?.id,
		checkoutSettled: checkoutFetched || checkoutFailed,
		checkoutId: checkout?.id,
		extraPending: canPreview && !preview && !previewFailed
	});

	// Já tem o produto da oferta: a tela não tem o que vender. Pula direto
	// para o próximo passo (downsell, se houver; senão o obrigado).
	useEffect(() => {
		if (preview?.alreadyPurchased && !isPreviewMode()) decline();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preview?.alreadyPurchased]);

	// Contagem da janela do one-click (o token vale 15 min).
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!preview?.expiresAt) return;
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [preview?.expiresAt]);
	const secondsLeft = preview?.expiresAt
		? Math.max(
				0,
				Math.floor((new Date(preview.expiresAt).getTime() - now) / 1000)
		  )
		: null;
	const isExpired =
		status === 'expired' || (secondsLeft !== null && secondsLeft <= 0);

	const charge = useMutation(paymentsApi.oneClickUpsell, {
		onSuccess: (result) => {
			// O obrigado lista a oferta no pedido (só se houve cobrança agora).
			if (!result?.alreadyPurchased) {
				addOrderExtra(thanksPath, mainReceipt, {
					kind: variant,
					title: preview?.content?.title || upsell?.title || 'Oferta',
					amount: preview?.amount ?? null,
					reference: result?.reference
				});
			}
			trackBehavior(
				variant === 'downsell'
					? 'downsell_accepted'
					: 'upsell_accepted',
				undefined,
				String(upsell?.contentId ?? '')
			);
			setStatus('success');
			// Deixa o "adicionado ao pedido" visível por um instante.
			window.setTimeout(goToThanks, 1800);
		},
		onError: (error: unknown) => {
			const response = (
				error as {
					response?: { status?: number; data?: { message?: string } };
				}
			)?.response;
			// A plataforma deixou de poder receber entre a compra principal e o
			// clique (409 `seller_not_active`, pay #619): nada foi cobrado. A
			// oferta some e o comprador segue para o obrigado — o mesmo de quem
			// já chega aqui com o params dizendo indisponível. Não vai ao Sentry.
			if (isSellerNotActiveError(error)) {
				goToThanks();
				return;
			}
			// Janela fechada: nada foi cobrado, e não há como tentar de novo.
			if (response?.status === 403) {
				setStatus('expired');
				return;
			}
			setStatus('error');
			const message =
				response?.data?.message ||
				'Não foi possível concluir a compra da oferta.';
			reportCheckoutError(error, 'upsell', message);
			setErrorMessage(message);
		}
	});

	const [dialogOpen, setDialogOpen] = useState(false);
	const closeDialog = useCallback(() => {
		setDialogOpen(false);
		setStatus((current) => (current === 'error' ? 'idle' : current));
	}, []);

	/** Só o "Confirmar pagamento" do diálogo cobra — nunca o botão da página. */
	const confirmCharge = () => {
		if (
			!stored.token ||
			!upsell?.contentId ||
			status === 'charging' ||
			isExpired ||
			isPreviewMode()
		)
			return;
		setStatus('charging');
		charge.mutate({
			token: stored.token,
			contentId: upsell.contentId,
			// Emenda a cobrança (fato de receita gravado pelo pay) no funil.
			sessionId: getBehaviorSessionId() ?? undefined
		});
	};

	// Cor do botão (builder) > cor principal do tema (texto automático) >
	// padrão do CSS.
	const accentStyle = checkoutButtonColors({
		settings: page?.settings,
		override: page?.settings?.confirmation?.buttonColor
	});

	const productTitle =
		preview?.content?.title ||
		offerContent?.title ||
		(isPreviewMode() ? 'Produto da oferta' : '');
	const productImage =
		upsell?.mediaUrl ||
		preview?.content?.image ||
		[...(offerContent?.images ?? [])].sort(
			(a, b) => Number(a.type) - Number(b.type)
		)[0]?.path ||
		null;
	// Valor COBRADO (pay) quando há compra; antes dele, o preço de tabela.
	const amount =
		preview?.amount ??
		(offerContent?.pricing?.price != null
			? Number(offerContent.pricing.price)
			: null);
	const amountLabel = amount != null ? formatCurrency(amount) : null;
	const oldPrice = Number(offerContent?.pricing?.oldPrice) || null;
	const oldPriceLabel =
		oldPrice && amount != null && oldPrice > amount
			? formatCurrency(oldPrice)
			: null;
	const cardLabel = preview?.card
		? `${preview.card.brand
				.charAt(0)
				.toUpperCase()}${preview.card.brand.slice(1)} •••• ${
				preview.card.last4
		  }`
		: isPreviewMode()
		? 'Visa •••• 4242'
		: 'o mesmo cartão da compra';
	const countdown =
		secondsLeft !== null
			? `${String(Math.floor(secondsLeft / 60)).padStart(
					2,
					'0'
			  )}:${String(secondsLeft % 60).padStart(2, '0')}`
			: null;

	const declineAndTrack = () => {
		trackBehavior(
			variant === 'downsell' ? 'downsell_declined' : 'upsell_declined',
			undefined,
			String(upsell?.contentId ?? '')
		);
		decline();
	};

	// O preço da compra só fica disponível com o preview do pay (valor real).
	const isLoading = !isPreviewMode() && canPreview && !preview;
	const acceptLabel = upsell?.acceptLabel || 'Sim, adicionar ao meu pedido';

	// Barra fixa com preço + botão quando o botão principal sai da tela
	// (blocos do builder abaixo da oferta empurram a página).
	const ctaRef = useRef<HTMLButtonElement>(null);
	const [ctaVisible, setCtaVisible] = useState(true);
	useEffect(() => {
		const node = ctaRef.current;
		if (!node || typeof IntersectionObserver === 'undefined') return;
		const observer = new IntersectionObserver(([entry]) =>
			setCtaVisible(entry.isIntersecting)
		);
		observer.observe(node);
		return () => observer.disconnect();
		// O botão só (re)aparece quando sai o skeleton ou a oferta expira.
	}, [isExpired, isReady]);

	// Sem token ou sem oferta configurada (acesso direto/expirado): não trava
	// o comprador — segue para o obrigado assim que a página resolver. No
	// preview do builder a página fica visível para edição, sem redirect.
	// Oferta que a autorização recusou (preview falhou) também segue.
	if (
		!isPreviewMode() &&
		page &&
		(!upsell?.enabled ||
			!upsell?.contentId ||
			!stored.token ||
			previewFailed ||
			paymentsUnavailable)
	) {
		goToThanks();
		return null;
	}

	const canOffer = !isExpired && status !== 'success';

	if (!isReady) {
		return (
			<div className="thanks-page upsell-page" aria-busy="true">
				<div className="container">
					<div className="thanks-hero upsell-offer page-skeleton">
						<span className="sk sk-pill" />
						<span className="sk sk-title" />
						<span className="sk sk-line" />
						<span className="sk sk-line is-short" />
						<div className="upsell-product">
							<span className="sk sk-thumb" />
							<div className="upsell-product-info">
								<span className="sk sk-line" />
								<span className="sk sk-price" />
							</div>
						</div>
						<span className="sk sk-button" />
						<span className="sk sk-line is-short" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`thanks-page upsell-page${
				!ctaVisible && canOffer ? ' has-sticky' : ''
			}`}
		>
			<div className="container">
				<div className="thanks-hero upsell-offer">
					<span className="upsell-badge">
						{variant === 'downsell'
							? 'Uma alternativa para você'
							: 'Oferta exclusiva pós-compra'}
					</span>
					<h1>
						{upsell?.title ||
							(variant === 'downsell'
								? 'Tudo bem! Que tal esta alternativa?'
								: 'Espere! Uma oferta para você')}
					</h1>
					<p className="upsell-message">
						{upsell?.message ||
							'Adicione esta oferta ao seu pedido com um clique — sem digitar o cartão de novo.'}
					</p>

					{/* Produto e preço SEMPRE visíveis — o comprador sabe o que
						leva e quanto paga antes de qualquer clique. */}
					<div className="upsell-product">
						{productImage ? (
							<img
								className="upsell-product-image"
								src={productImage}
								alt=""
							/>
						) : (
							<span className="upsell-product-image is-empty" />
						)}
						<div className="upsell-product-info">
							<span className="upsell-product-title">
								{productTitle || (
									<span className="upsell-skeleton is-title" />
								)}
							</span>
							<span className="upsell-product-price">
								{oldPriceLabel && (
									<s className="upsell-old-price">
										{oldPriceLabel}
									</s>
								)}
								{amountLabel ?? (
									<span className="upsell-skeleton is-price" />
								)}
								<small> à vista no cartão · 1x</small>
							</span>
						</div>
					</div>

					{isExpired ? (
						<div className="upsell-notice" role="status">
							<Timer size={18} />O tempo desta oferta acabou. Nada
							foi cobrado.
							<button
								type="button"
								className="thanks-cta upsell-accept"
								style={accentStyle}
								onClick={decline}
							>
								Continuar
							</button>
						</div>
					) : (
						<div className="upsell-cta-area">
							<button
								ref={ctaRef}
								type="button"
								className="thanks-cta upsell-accept"
								style={accentStyle}
								disabled={isLoading || status === 'success'}
								onClick={() => setDialogOpen(true)}
							>
								{acceptLabel}
								{amountLabel && (
									<span className="upsell-accept-price">
										· {amountLabel}
									</span>
								)}
							</button>

							<p className="upsell-card">
								<CreditCard size={14} />
								Cobrança única em {cardLabel}
								<span className="upsell-dot">·</span>
								<Lock size={12} /> você confirma antes de pagar
							</p>
							{countdown && (
								<p className="upsell-timer">
									<Timer size={13} /> Oferta válida por{' '}
									<strong>{countdown}</strong>
								</p>
							)}

							<button
								type="button"
								className="upsell-decline"
								onClick={declineAndTrack}
							>
								{upsell?.declineLabel ||
									'Não, obrigado — seguir sem a oferta'}
							</button>
						</div>
					)}
				</div>

				{/* Blocos livres do builder (page.upsell) — argumentos de
					venda abaixo do cartão da oferta. */}
				<PageBlocks
					page={
						page ? { ...page, blocks: page[variant] ?? [] } : page
					}
					checkoutId={checkout?.id}
				/>
				{/* Área de solta no builder mesmo com a página vazia. */}
				{isPreviewMode() && !page?.[variant]?.length && (
					<div className="page-blocks" style={{ minHeight: 140 }} />
				)}
			</div>

			{/* Preço e botão fixos no rodapé quando a oferta sai da tela. */}
			{!ctaVisible && canOffer && (
				<div
					className="upsell-sticky"
					role="region"
					aria-label="Oferta"
				>
					<div className="upsell-sticky-info">
						<span>{productTitle}</span>
						<strong>{amountLabel}</strong>
					</div>
					<button
						type="button"
						className="upsell-sticky-cta"
						style={accentStyle}
						disabled={isLoading}
						onClick={() => setDialogOpen(true)}
					>
						Adicionar ao pedido
					</button>
				</div>
			)}

			<ConfirmDialog
				open={dialogOpen || status === 'success'}
				state={isExpired ? 'expired' : status}
				errorMessage={errorMessage}
				productTitle={productTitle}
				productImage={productImage}
				amountLabel={amountLabel}
				cardLabel={cardLabel}
				countdown={countdown}
				accentStyle={accentStyle}
				isPreview={isPreviewMode()}
				onConfirm={confirmCharge}
				onClose={closeDialog}
				onSkip={declineAndTrack}
			/>
		</div>
	);
};
